import type { CeoDebtInstrument } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  formatDateRu,
  formatMillions,
  formatMoney,
  INSTRUMENT_TYPE_LABELS,
} from '../ceoFormat';
import {
  ContractNumberText,
  CreditorNameText,
  LeasedAssetText,
  LimitSubtext,
  LimitText,
  MutedBankText,
  MutedDateText,
  NextAmountText,
  NextDateText,
  RateSubtext,
  RateText,
  Section,
  Table,
  TableWrap,
  Title,
  TitleCell,
  TitleText,
} from './styles';

interface DebtInstrumentsTableProps {
  instruments: CeoDebtInstrument[];
  onSelectInstrument: (id: string) => void;
}

export function DebtInstrumentsTable({
  instruments,
  onSelectInstrument,
}: DebtInstrumentsTableProps) {
  return (
    <Section>
      <Title>
        Реестр договоров кредитов, лизинга и банковских гарантий (нажмите «Вся информация» для
        открытия полного паспорта и графика)
      </Title>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Тип / № договора</th>
              <th>Кредитор / Лизингодатель</th>
              <th>Наименование / Предмет лизинга</th>
              <th>Лимит / Остаток долга</th>
              <th>Ставка</th>
              <th>Следующий платёж</th>
              <th>Ковенанты</th>
              <th>Действие</th>
            </tr>
          </thead>
          <tbody>
            {instruments.map((inst) => (
              <tr key={inst.id}>
                <td>
                  <Badge
                    $tone={inst.instrument_type === 'leasing' ? 'info' : 'warning'}
                  >
                    {INSTRUMENT_TYPE_LABELS[inst.instrument_type] ?? inst.instrument_type}
                  </Badge>
                  <ContractNumberText>№ {inst.contract_number}</ContractNumberText>
                  <MutedDateText>до {formatDateRu(inst.end_date)}</MutedDateText>
                </td>
                <td>
                  <CreditorNameText>{inst.creditor_name}</CreditorNameText>
                  <MutedBankText>{inst.settlement_bank_name ?? ''}</MutedBankText>
                </td>
                <TitleCell>
                  <TitleText>{inst.title}</TitleText>
                  {inst.leased_asset_name && (
                    <LeasedAssetText>
                      Техника ({inst.leased_asset_count} ед.): {inst.leased_asset_name}
                    </LeasedAssetText>
                  )}
                </TitleCell>
                <td>
                  {inst.instrument_type === 'bank_guarantee' ? (
                    <>
                      <LimitText>
                        Лимит БГ {formatMillions(inst.total_limit)}
                      </LimitText>
                      <LimitSubtext>
                        забалансовое обяз. (долг {formatMillions(inst.principal_balance)})
                      </LimitSubtext>
                    </>
                  ) : (
                    <>
                      <LimitText>
                        {formatMillions(inst.principal_balance)}
                      </LimitText>
                      <LimitSubtext>
                        из лимита {formatMillions(inst.total_limit)}
                      </LimitSubtext>
                    </>
                  )}
                </td>
                <td>
                  <RateText>{inst.effective_rate_pct.toFixed(2)}%</RateText>
                  <RateSubtext>
                    {inst.instrument_type === 'bank_guarantee'
                      ? 'Комиссия БГ'
                      : inst.rate_type === 'floating_cbr'
                        ? `КС ЦБ + ${inst.margin_pct}%`
                        : inst.rate_type === 'subsidized'
                          ? 'Льготная'
                          : 'Фикс.'}
                  </RateSubtext>
                </td>
                <td>
                  {inst.next_payment_date ? (
                    <>
                      <NextDateText>
                        {formatDateRu(inst.next_payment_date)}
                      </NextDateText>
                      <NextAmountText>
                        {formatMoney(inst.next_payment_amount)}
                      </NextAmountText>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <Badge
                    $tone={
                      inst.covenant_status === 'ok'
                        ? 'success'
                        : inst.covenant_status === 'breach'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {inst.covenant_status === 'ok' ? 'Соблюдены' : 'Контроль КАСКО/отчётности'}
                  </Badge>
                </td>
                <td>
                  <Button
                    $size="sm"
                    onClick={() => onSelectInstrument(inst.id)}
                  >
                    Вся информация и график
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
